import { Theme } from "@mui/material/styles";

import { themeColors } from "../theme/colorTokens";

/**
 * Common DataGrid styling utility
 * Creates consistent styling for all DataGrids in the application
 *
 * Usage:
 * <DataGrid sx={getDataGridStyles(theme)} ... />
 */
export const getDataGridStyles = (theme: Theme) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${themeColors.border.light}`,
  fontSize: "16px",
  width: "100%",

  // Cell styling
  "& .MuiDataGrid-cell": {
    backgroundColor: theme.palette.background.paper,
  },

  // Header styling - using gray 50 background
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: themeColors.background.default,
    color: theme.palette.text.primary,
    fontWeight: 600,
    fontSize: "16px",
    borderRadius: "12px 12px 0 0",
  },

  "& .MuiDataGrid-columnHeader": {
    backgroundColor: themeColors.background.default,
    "&:focus": {
      outline: "none",
    },
    "&:focus-within": {
      outline: "none",
    },
  },

  "& .MuiDataGrid-columnHeaderTitle": {
    color: theme.palette.text.primary,
    fontWeight: 600,
  },

  // Row styling - paper white background
  "& .MuiDataGrid-row": {
    backgroundColor: theme.palette.background.paper,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "&.Mui-selected": {
      backgroundColor: theme.palette.action.selected,
      "&:hover": {
        backgroundColor: theme.palette.action.selected,
      },
    },
  },

  // Footer styling
  "& .MuiDataGrid-footerContainer": {
    backgroundColor: theme.palette.background.paper,
    borderRadius: "0 0 12px 12px",
  },

  // Interactive elements styling
  "& .MuiDataGrid-selectedRowCount": {
    color: theme.palette.text.secondary,
  },

  "& .MuiDataGrid-columnSeparator": {
    color: themeColors.border.light,
  },

  "& .MuiDataGrid-sortIcon": {
    color: theme.palette.text.secondary,
  },

  "& .MuiDataGrid-menuIconButton": {
    color: theme.palette.text.secondary,
  },

  "& .MuiDataGrid-filterIcon": {
    color: theme.palette.text.secondary,
  },

  // Pagination styling
  "& .MuiTablePagination-root": {
    color: theme.palette.text.primary,
  },

  "& .MuiTablePagination-selectIcon": {
    color: theme.palette.text.secondary,
  },

  "& .MuiTablePagination-displayedRows": {
    color: theme.palette.text.primary,
    fontWeight: 500,
    fontSize: "0.875rem",
  },

  "& .MuiTablePagination-select": {
    color: theme.palette.text.primary,
    fontWeight: 500,
  },

  "& .MuiTablePagination-selectLabel": {
    color: theme.palette.text.primary,
    fontWeight: 500,
  },

  "& .MuiTablePagination-actions button": {
    color: theme.palette.text.secondary,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "&.Mui-disabled": {
      color: theme.palette.text.disabled,
    },
  },

  "& .MuiTablePagination-toolbar": {
    paddingLeft: "16px",
    paddingRight: "16px",
    minHeight: "56px",
  },
});

/**
 * Default DataGrid props for consistent behavior across the app
 */
export const DEFAULT_DATA_GRID_PAGE_SIZE = 5;
export const DEFAULT_DATA_GRID_PAGE_SIZE_OPTIONS = [5, 10, 25];

export const defaultDataGridProps = {
  disableRowSelectionOnClick: true,
  autoHeight: true,
  initialState: {
    pagination: {
      paginationModel: {
        pageSize: DEFAULT_DATA_GRID_PAGE_SIZE,
      },
    },
  },
  pageSizeOptions: DEFAULT_DATA_GRID_PAGE_SIZE_OPTIONS,
};
